import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useVoteModalStore } from '../../stores/voteModalStore';
import { getOptimizedImageUrl } from '../../lib/imageUtils';

interface AwardContestantBlocksProps {
  contestId: string;
  onVoteClick?: (contestant: any) => void;
}

interface Contestant {
  id: string;
  name: string;
  photo_url: string;
  unique_code: string;
  votes_count: number;
  rank: number;
}

// Styles pour l'animation
const fadeInAnimation = `
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}
`;

export const AwardContestantBlocks: React.FC<AwardContestantBlocksProps> = ({
  contestId,
  onVoteClick
}) => {
  const [selectedContestant, setSelectedContestant] = useState<Contestant | null>(null);
  const navigate = useNavigate();
  const setVoteModalOpen = useVoteModalStore(state => state.setOpen);

  const { data: contestants, isLoading } = useQuery({
    queryKey: ['contestants', contestId],
    queryFn: async () => {
      // Utilisation de la fonction Supabase optimisée
      const { data, error } = await supabase.rpc('get_contest_contestants_v1', { p_contest_id: contestId });
      if (error) throw error;
      if (!data?.length) return [];
      return data;
    },
    refetchInterval: 30000 // Rafraîchir toutes les 30 secondes
  });

  // État pour gérer l'affichage du candidat agrandi au centre
  const [expandedContestant, setExpandedContestant] = useState<Contestant | null>(null);

  const handleVoteClick = (contestant: Contestant, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Ouvrir la modale de vote en utilisant le store global
    setVoteModalOpen(true, contestant.id);
    
    // Rediriger vers la page du candidat avec un délai pour permettre à la modale de s'afficher d'abord
    setTimeout(() => {
      navigate(`/${contestant.unique_code}`);
    }, 100);
    
    if (onVoteClick) {
      onVoteClick(contestant);
    }
  };
  
  // Fonction pour afficher le candidat agrandi au centre
  const handleExpandContestant = (contestant: Contestant, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedContestant(contestant);
  };
  
  // Fonction pour fermer l'affichage agrandi
  const handleCloseExpanded = () => {
    setExpandedContestant(null);
  };

  const handleViewProfile = (contestant: Contestant, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Rediriger vers la page du candidat
    navigate(`/${contestant.unique_code}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400" />
      </div>
    );
  }

  if (!contestants?.length) {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">Aucun participant inscrit pour le moment</p>
      </div>
    );
  }

  // Diviser les candidats en blocs de 6 pour desktop et 3 pour mobile
  const getBlocks = () => {
    const isMobile = window.innerWidth <= 640;
    const blockSize = isMobile ? 3 : 6;
    const blocks = [];
    
    for (let i = 0; i < contestants.length; i += blockSize) {
      blocks.push(contestants.slice(i, i + blockSize));
    }
    
    return blocks;
  };

  // Calculer les positions pour une étoile à 3 ou 6 branches
  const getStarPositions = (numPoints: number, blockLength?: number) => {
    const positions = [];
    const radius = numPoints === 3 ? 100 : 240;
    // Le nombre de points à utiliser (ajusté en fonction du nombre réel de candidats)
    const adjustedNumPoints = blockLength && blockLength < numPoints ? blockLength : numPoints;
    
    for (let i = 0; i < adjustedNumPoints; i++) {
      const angle = (i * 2 * Math.PI) / adjustedNumPoints - Math.PI / 2;
      positions.push({
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        rotation: (angle * 180) / Math.PI + 90
      });
    }
    return positions;
  };

  const desktopPositions = getStarPositions(6);
  const mobilePositions = getStarPositions(3);

  return (
    <div className="w-full py-8 sm:py-20 relative overflow-x-hidden">
      {/* Styles pour l'animation */}
      <style dangerouslySetInnerHTML={{ __html: fadeInAnimation + `
        /* Masquer la barre de défilement sur tous les appareils */
        ::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }
        
        /* Pour Firefox */
        * {
          scrollbar-width: none;
        }
        
        /* Pour IE et Edge */
        * {
          -ms-overflow-style: none;
        }
      `}} />
      {/* Overlay pour afficher le candidat agrandi au centre */}
      {expandedContestant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Arrière-plan flouté */}
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-lg" 
            onClick={handleCloseExpanded}
          />
          
          {/* Conteneur du candidat agrandi */}
          <div className="relative z-10 max-w-2xl w-full mx-4 animate-fadeIn max-h-[90vh] overflow-y-auto">
            {/* Bouton de fermeture */}
            <button 
              className="absolute -top-12 right-0 text-white/80 hover:text-white p-2 text-2xl z-20"
              onClick={handleCloseExpanded}
            >
              &times;
            </button>
            
            <div className="bg-black/40 backdrop-blur-md rounded-2xl overflow-hidden border border-yellow-400/30 shadow-[0_0_30px_rgba(234,179,8,0.3)]">
              <div className="relative aspect-square overflow-hidden">
                {/* Image */}
                <img 
                  src={expandedContestant.photo_url || '/placeholder.jpg'} 
                  alt={expandedContestant.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Badge de position */}
                <div className={`
                  absolute top-4 right-4
                  w-16 h-16 rounded-full
                  flex items-center justify-center
                  text-2xl font-bold
                  border-[3px] border-[#111216]
                  shadow-lg
                  ${expandedContestant.rank === 1
                    ? 'bg-yellow-400 text-black'
                    : expandedContestant.rank === 2
                    ? 'bg-slate-300 text-black'
                    : expandedContestant.rank === 3
                    ? 'bg-amber-700 text-white'
                    : 'bg-[#1e1b4b] text-white'
                  }
                `}>
                  {expandedContestant.rank}
                </div>
                
                {/* Couronne pour le top 3 */}
                {expandedContestant.rank <= 3 && (
                  <div className={`
                    absolute top-4 left-4 text-4xl
                    ${expandedContestant.rank === 1
                      ? 'text-yellow-400'
                      : expandedContestant.rank === 2
                      ? 'text-slate-300'
                      : 'text-amber-700'
                    }
                  `}>
                    👑
                  </div>
                )}
              </div>
              
              {/* Informations et actions */}
              <div className="p-6 bg-gradient-to-b from-black/60 to-black/90">
                <h3 className="text-2xl font-playfair font-bold text-white mb-2">{expandedContestant.name}</h3>
                <p className="text-yellow-400 text-lg mb-4">{expandedContestant.votes_count} votes</p>
                
                <div className="flex gap-4 mt-6">
                  <a 
                    href={`/${expandedContestant.unique_code}`}
                    className="flex-1 py-3 px-4 rounded-full bg-black/50 backdrop-blur-sm border border-yellow-400/50 flex items-center justify-center gap-2 text-yellow-400 hover:bg-black/70 hover:border-yellow-400 transition-all duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <span>Voir le profil</span>
                  </a>
                  
                  <button 
                    onClick={(e) => handleVoteClick(expandedContestant, e)}
                    className="flex-1 py-3 px-4 rounded-full bg-black/50 backdrop-blur-sm border border-yellow-400/50 flex items-center justify-center gap-2 text-yellow-400 hover:bg-black/70 hover:border-yellow-400 transition-all duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                    <span>Voter</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Conteneur des blocs étoiles */}
      <div className="grid grid-cols-1 gap-20 sm:gap-40 px-4 sm:px-10 overflow-x-hidden">
        {getBlocks().map((block, blockIndex) => (
          <motion.div
            key={blockIndex}
            className="relative h-[400px] sm:h-[700px]"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              duration: 0.5,
              delay: blockIndex * 0.2,
              type: "spring",
              stiffness: 100
            }}
          >
            {/* Centre de l'étoile - effet lumineux */}
            <div 
              className="absolute left-1/2 top-1/2 w-20 sm:w-40 h-20 sm:h-40 -translate-x-1/2 -translate-y-1/2"
              style={{
                background: 'radial-gradient(circle, rgba(250,204,21,0.4) 0%, transparent 70%)',
                filter: 'blur(20px)'
              }}
            />

            {/* Photos sur les branches de l'étoile */}
            {block.map((contestant, index) => {
              const isMobile = window.innerWidth <= 640;
              
              // Générer dynamiquement les positions en fonction du nombre réel de candidats dans le bloc
              const dynamicPositions = isMobile 
                ? getStarPositions(3, block.length)
                : getStarPositions(6, block.length);
                
              // S'assurer que l'index ne dépasse pas le nombre de positions disponibles
              const positionIndex = Math.min(index, dynamicPositions.length - 1);
              const position = dynamicPositions[positionIndex];

              return (
                <motion.div
                  key={contestant.id}
                  className="absolute cursor-pointer"
                  style={{
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) rotate(${position.rotation}deg)`,
                    width: isMobile ? '120px' : '220px',
                    height: isMobile ? '120px' : '220px'
                  }}
                  whileHover={{ 
                    zIndex: 10
                  }}
                >
                  {/* Container de la photo */}
                  <div className="relative w-full h-full transform -rotate-[var(--rotation)]" style={{ '--rotation': `${position.rotation}deg` } as any}>
                    {/* Branche de l'étoile */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-t from-yellow-400/30 to-transparent transition-opacity duration-300"
                      style={{
                        clipPath: 'polygon(50% 0%, 65% 40%, 100% 40%, 72% 60%, 85% 100%, 50% 75%, 15% 100%, 28% 60%, 0% 40%, 35% 40%)'
                      }}
                    />

                    {/* Photo du candidat avec agrandissement au clic */}
                    <div className="absolute inset-[4%] overflow-visible">
                      <div 
                        className="relative w-full h-full transition-all duration-300 ease-out z-10 group cursor-pointer"
                        onClick={(e) => handleExpandContestant(contestant, e)}
                      >
                        {/* Boutons d'action qui apparaissent au survol - placés à l'extérieur et collés au cercle */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 pointer-events-none">
                          {/* Bouton Voir - à gauche */}
                          <div className="absolute left-0 top-1/2 -translate-x-[95%] -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-auto">
                            <a 
                              href={`/${contestant.unique_code}`}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/70 backdrop-blur-sm border border-yellow-400/50 flex items-center justify-center text-yellow-400 shadow-lg transform transition-transform duration-300 hover:scale-110 hover:bg-black/80 hover:border-yellow-400"
                              aria-label="Voir le profil"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </a>
                            <span className="text-xs text-yellow-400/90 font-medium bg-black/50 px-2 py-0.5 rounded-full backdrop-blur-sm whitespace-nowrap">Voir</span>
                          </div>
                          
                          {/* Bouton Voter - à droite */}
                          <div className="absolute right-0 top-1/2 translate-x-[95%] -translate-y-1/2 flex flex-col items-center gap-1 pointer-events-auto">
                            <button 
                              onClick={(e) => handleVoteClick(contestant, e)}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/70 backdrop-blur-sm border border-yellow-400/50 flex items-center justify-center text-yellow-400 shadow-lg transform transition-transform duration-300 hover:scale-110 hover:bg-black/80 hover:border-yellow-400"
                              aria-label="Voter"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                              </svg>
                            </button>
                            <span className="text-xs text-yellow-400/90 font-medium bg-black/50 px-2 py-0.5 rounded-full backdrop-blur-sm whitespace-nowrap">Voter</span>
                          </div>
                        </div>
                        {/* Container principal de la photo */}
                        <div className={`
                          relative w-full h-full rounded-full overflow-hidden
                          ${contestant.rank <= 3 
                            ? 'border-[8px]' 
                            : 'border-[6px]'
                          }
                          ${contestant.rank === 1
                            ? 'border-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.5)]'
                            : contestant.rank === 2
                            ? 'border-slate-300 shadow-[0_0_15px_rgba(226,232,240,0.3)]'
                            : contestant.rank === 3
                            ? 'border-amber-700 shadow-[0_0_15px_rgba(180,83,9,0.3)]'
                            : 'border-yellow-400/50'
                          }
                        `}>
                        
                        
                          {/* Couronne pour le top 3 */}
                          {contestant.rank <= 3 && (
                            <div className={`
                              absolute -top-6 left-1/2 -translate-x-1/2 text-2xl
                              ${contestant.rank === 1
                                ? 'text-yellow-400'
                                : contestant.rank === 2
                                ? 'text-slate-300'
                                : 'text-amber-700'
                              }
                            `}>
                              👑
                            </div>
                          )}

                          {/* Photo */}
                          <img
                            src={getOptimizedImageUrl(contestant.photo_url, 200, 200)}
                            alt={contestant.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Badge de position */}
                        <div className={`
                          absolute -top-1 -right-1
                          w-9 h-9 rounded-full
                          flex items-center justify-center
                          text-base font-bold
                          border-[3px] border-[#111216]
                          shadow-lg
                          ${contestant.rank === 1
                            ? 'bg-yellow-400 text-black'
                            : contestant.rank === 2
                            ? 'bg-slate-300 text-black'
                            : contestant.rank === 3
                            ? 'bg-amber-700 text-white'
                            : 'bg-[#1e1b4b] text-white'
                          }
                        `}>
                          {contestant.rank}
                        </div>

                        {/* Nom du candidat */}
                        <div className="
                          absolute -bottom-8 left-1/2 -translate-x-1/2 
                          text-center text-white font-medium
                          text-xs sm:text-base
                          px-2 py-1
                          min-w-max max-w-[160px] sm:max-w-[220px]
                          bg-black/30 backdrop-blur-sm rounded-full
                          shadow-md
                        ">
                          <span className="hyphens-auto break-words">{contestant.name}</span>
                        </div>

                        {/* Drapeau des votes avec animation */}
                        <div className="absolute -bottom-1 -right-3 transform transition-all duration-500 group-hover:scale-90">
                          {/* Mât du drapeau avec effet 3D */}
                          <div className={`
                            absolute right-4 bottom-0 w-[3px] h-16 origin-bottom
                            ${contestant.rank === 1
                              ? 'bg-gradient-to-r from-yellow-600 to-yellow-400'
                              : contestant.rank === 2
                              ? 'bg-gradient-to-r from-slate-400 to-slate-300'
                              : contestant.rank === 3
                              ? 'bg-gradient-to-r from-amber-800 to-amber-700'
                              : 'bg-gradient-to-r from-gray-600 to-gray-400'
                            }
                          `}>
                            {/* Ombre du mât */}
                            <div className="absolute inset-0 w-[1px] h-full bg-black/20 -right-[2px]" />
                          </div>
                          
                          {/* Drapeau avec effet 3D */}
                          <div className="relative">
                            {/* Base du drapeau */}
                            <div className={`
                              relative px-3 py-1.5 rounded-sm shadow-[2px_2px_4px_rgba(0,0,0,0.3)]
                              ${contestant.rank === 1
                                ? 'bg-yellow-400 text-black'
                                : contestant.rank === 2
                                ? 'bg-slate-300 text-black'
                                : contestant.rank === 3
                                ? 'bg-amber-700 text-white'
                                : 'bg-[#1e1b4b] text-white'
                              }
                            `}>
                              {/* Effet de pli du drapeau */}
                              <div className="absolute inset-0 bg-gradient-to-l from-black/10 via-transparent to-black/5" />
                              
                              {/* Triangle décoratif */}
                              <div 
                                className="absolute -right-2 top-0 w-0 h-0"
                                style={{
                                  borderLeft: contestant.rank === 1
                                    ? '8px solid #facc15'
                                    : contestant.rank === 2
                                    ? '8px solid #cbd5e1'
                                    : contestant.rank === 3
                                    ? '8px solid #b45309'
                                    : '8px solid #1e1b4b',
                                  borderBottom: '12px solid transparent'
                                }}
                              />
                              
                              {/* Nombre de votes */}
                              <span className={`
                                text-sm font-bold tracking-wider
                                ${contestant.rank <= 3 ? 'drop-shadow' : ''}
                              `}>
                                {contestant.votes_count} votes
                              </span>
                            </div>
                          </div>

                          {/* Point d'ancrage du drapeau */}
                          <div className="absolute right-4 bottom-0 w-2 h-2 rounded-full bg-[#fbbf24] shadow-sm z-10" />
                        </div>
                      </div>

                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Lignes de connexion */}
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
              style={{ transform: 'translate(-50%, -50%)', left: '50%', top: '50%' }}
            >
              {(window.innerWidth <= 640 
                ? getStarPositions(3, block.length) 
                : getStarPositions(6, block.length)
              ).map((pos, i) => (
                <line
                  key={i}
                  x1="50%"
                  y1="50%"
                  x2={`${50 + pos.x / Math.sqrt(pos.x * pos.x + pos.y * pos.y) * 20}%`}
                  y2={`${50 + pos.y / Math.sqrt(pos.x * pos.x + pos.y * pos.y) * 20}%`}
                  stroke="rgba(250,204,21,0.2)"
                  strokeWidth="3"
                />
              ))}
            </svg>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
